import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, DialogActions, DialogContent, Grid, MenuItem, Popover, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SearchField from '@dyl/components/SearchField';
import { FilterList as FilterIcon } from '@mui/icons-material';
import TokenStatuses from '@dyl/constants/TokenStatuses';
import { noop } from 'lodash';
import { DatePicker } from '@mui/x-date-pickers';
import TokenTypes from '@dyl/constants/TokenTypes';
import { useTests, useCentres, useLegacy } from '@dyl/systems/TestsManager';
import useChangeEvent from '@dyl/hooks/useChangeEvent';

const useStyles = makeStyles(theme => ({
    filterButton: {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
    },
    filterMenu: {
        width: 548,
    },
}));

export default function FilterBlock({
    filter = {},
    onFilterChange = noop,
    search = '',
    onSearch = noop,
    ...other
}) {
    const classes = useStyles(other);

    const buttonRef = useRef();
    const [isMenuOpen, setMenuOpen] = useState(false);

    const useField = field => {
        const value = filter[field] ?? '';

        const onChange = useCallback(e => {
            onFilterChange(prev => {
                const next = { ...prev };
                const value = typeof e?.target?.value !== 'undefined' ? e.target.value : e;

                if (value) next[field] = value;
                else delete next[field];

                return next;
            });
        }, [onFilterChange, field]);

        return [value, onChange];
    };

    const [status, onStatusChange] = useField('status');
    const [type, onTypeChange] = useField('type');
    const [description, onDescriptionChange] = useField('description');

    const [, onTestIdChange] = useField('test_group_id');
    const [, onTestCentreChange] = useField('test_centre_number');
    const [, onTestLegacyChange] = useField('legacy_number');
    const [testName, setTestName] = useState('');
    const onTestNameChange = useChangeEvent(setTestName);
    const [centreNumber, setCentreNumber] = useState('');
    const onCentreNumberChange = useChangeEvent(setCentreNumber);
    const [legacyNumber, setLegacyNumber] = useState('');
    const onLegacyNumberChange = useChangeEvent(setLegacyNumber);

    const { tests, testsStatus } = useTests();
    const { centres, centreStatus } = useCentres();
    const { legacies = centres, legacyStatus } = useLegacy();

    const test = useMemo(() => {
    return tests.filter(t => !!t.name).find(t => t.name.trim().toLowerCase() === testName.trim().toLowerCase());
    }, [testName, tests]);

    const centre = useMemo(() => {
        return centres.filter(c => !!c.test_centre_number).find(c => c.test_centre_number.trim().toLowerCase() === centreNumber.trim().toLowerCase());
    }, [centreNumber, centres]);

    const legacy = useMemo(() => {
        return legacies.filter(c => !!c.legacy_number).find(c => c.legacy_number === legacyNumber);
    }, [legacyNumber, legacies]);

    useLayoutEffect(() => {
        onTestIdChange(testName ? test?.test_group_id ?? '__INVALID__' : '');
        onTestCentreChange(centreNumber ? centre?.test_centre_number ?? '__INVALID__' : '');
        onTestLegacyChange(legacyNumber ? legacy?.legacy_number ?? '__INVALID__' : '');
    }, [test, testName, centre, centreNumber, legacy, legacyNumber]);

    const dateFields = [
        ['Date Created From', useField('createdFrom')],
        ['Date Created To', useField('createdTo')],
        ['Date Redeemed From', useField('redeemedFrom')],
        ['Date Redeemed To', useField('redeemedTo')],
    ];

    const onClear = () => {
        onFilterChange({});
        setTestName(''); // Internal field, so must be cleared manually
        setCentreNumber('');
        setLegacyNumber('');
    }

    return (
        <Grid container alignItems='center' {...other}>
            <Grid item xs={4}>
                <SearchField
                    fullWidth
                    value={search}
                    onChange={e => onSearch(e.target.value)}
                />
            </Grid>

            <Grid item xs={2}>
                <Button
                    className={classes.filterButton}
                    variant='outlined'
                    color='primary'
                    onClick={() => setMenuOpen(prev => !prev)}
                    ref={buttonRef}
                >
                    Filter <FilterIcon />
                </Button>
            </Grid>

            {/* FILTER MENU */}
            <Popover
                open={isMenuOpen}
                anchorEl={() => buttonRef.current}
                onClose={() => setMenuOpen(false)}
                anchorReference='anchorEl'
                anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                classes={{ paper: classes.filterMenu }}
            >
                <DialogContent>
                    <Grid container spacing={2}>
                        {/* STATUS */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                label='Status'
                                select
                                fullWidth
                                value={status}
                                onChange={onStatusChange}
                            >
                                {TokenStatuses.map(({ label, value }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* TYPE */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                label='Type'
                                select
                                fullWidth
                                value={type}
                                onChange={onTypeChange}
                            >
                                <MenuItem value=''>
                                    None
                                </MenuItem>
                                {TokenTypes.map(({ label, value }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* DESCRIPTION */}
                        <Grid item xs={12}>
                            <TextField
                                label='Description'
                                value={description}
                                onChange={onDescriptionChange}
                                fullWidth
                            />
                        </Grid>

                        {/* DATES */}
                        {dateFields.map(([label, [value, onChange]], i) => (
                            <Grid item xs={12} md={6} key={i}>
                                <DatePicker
                                    label={label}
                                    value={value || null}
                                    onChange={onChange}
                                    inputFormat='dd/MM/yyyy'
                                    fullWidth
                                    renderInput={props => <TextField  {...props} />}
                                />
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <TextField
                                value={testName}
                                onChange={onTestNameChange}
                                label='Test ID'
                                disabled={testsStatus !== 'complete'}
                                helperText={(
                                    testsStatus !== 'complete' ? 'Tests not loaded'
                                    : 'Must be exact (case insensitive)'
                                )}
                                error={(
                                    testsStatus !== 'complete'
                                    || (!!testName && !test)
                                )}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                value={centreNumber}
                                onChange={onCentreNumberChange}
                                label='Centre Number'
                                disabled={centreStatus !== 'complete'}
                                helperText={(
                                    centreStatus !== 'complete' ? 'Centres not loaded'
                                    : 'Must be exact (case insensitive)'
                                )}
                                error={(
                                    centreStatus !== 'complete'
                                    || (!!centreNumber && !centre)
                                )}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                value={legacyNumber}
                                onChange={onLegacyNumberChange}
                                label='Candidate Number'
                                disabled={legacyStatus !== 'complete'}
                                helperText={(
                                    legacyStatus !== 'complete' ? 'Candidate numbers not loaded'
                                    : 'Must be exact (case insensitive)'
                                )}
                                error={(
                                    legacyStatus !== 'complete'
                                    || (!!legacyNumber && !legacy)
                                )}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClear} color='primary'>
                        Clear
                    </Button>
                </DialogActions>
            </Popover>
        </Grid>
    );
}